import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { updateDoc, Timestamp } from "firebase/firestore";
import PropertyContext from "../contexts/PropertyContext";
import { propertyDoc } from "../utils/firestorePaths";
import { useOptimizedRoomFiltering } from "../hooks/useOptimizedRoomFiltering";
import { ROOM_FILTER_TYPES } from "../constants/roomConstants";

const FrontDeskRooms = ({ user }) => {
  const { rooms, selectedProperty, selectedClient } = useContext(PropertyContext);
  const [isUpdating, setIsUpdating] = useState(null);

  // Use existing filtering hook
  const {
    allFilteredRooms,
    selectionOption,
    searchTerm,
    handleFilterChange,
    handleSearchChange,
  } = useOptimizedRoomFiltering(rooms, 10000);

  // Wrap handlers to work with Material-UI event objects
  const onFilterChange = (event) => {
    handleFilterChange(event.target.value);
  };

  const onSearchChange = (event) => {
    handleSearchChange(event.target.value);
  };

  // Handle check-in/check-out actions
  const handleStatusChange = async (room, newStatus) => {
    setIsUpdating(room.id);
    try {
      const roomRef = propertyDoc(
        selectedClient,
        selectedProperty,
        "units",
        room.id
      );

      await updateDoc(roomRef, {
        status: newStatus,
        lastUpdated: Timestamp.now(),
        lastUpdatedBy: user?.fullName || user?.email || "Unknown",
      });

      console.log(`Room ${room.displayName} status updated to ${newStatus}`);
    } catch (error) {
      console.error("Error updating room status:", error);
      alert(`Error updating room: ${error.message}`);
    } finally {
      setIsUpdating(null);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusColors = {
      Occupied: "primary",
      Up: "success",
      Down: "error",
      Facilities: "warning",
      Vacant: "default",
    };
    return statusColors[status] || "default";
  };

  // Render action button for a room
  const renderActionButton = (room) => {
    const isLoading = isUpdating === room.id;

    // Show appropriate action button based on status
    if (room.status === "Occupied") {
      return (
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={() => handleStatusChange(room, "Vacant")}
          disabled={isLoading}
          sx={{ minWidth: 120 }}
        >
          {isLoading ? "Updating..." : "Check Out"}
        </Button>
      );
    } else if (room.status === "Up" || room.status === "Vacant") {
      return (
        <Button
          variant="contained"
          color="success"
          size="small"
          onClick={() => handleStatusChange(room, "Occupied")}
          disabled={isLoading}
          sx={{ minWidth: 120 }}
        >
          {isLoading ? "Updating..." : "Check In"}
        </Button>
      );
    } else {
      // Room is Down or Facilities - no action available
      return (
        <Chip
          label="Not Available"
          size="small"
          color="default"
          variant="outlined"
        />
      );
    }
  };

  return (
    <Box sx={{ height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box mb={2}>
        <Typography variant="h5" fontWeight="600" gutterBottom>
          Room Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Check guests in and out
        </Typography>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Search */}
        <TextField
          label="Search Rooms"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={onSearchChange}
          sx={{ minWidth: 200 }}
          placeholder="Room number..."
        />

        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={selectionOption || ""}
            onChange={onFilterChange}
            label="Filter by Status"
          >
            {Object.values(ROOM_FILTER_TYPES).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Room Count */}
        <Chip
          label={`${allFilteredRooms.length} Rooms`}
          color="primary"
          variant="outlined"
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ flexGrow: 1, overflow: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="600">
                  Room
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="600">
                  Status
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" fontWeight="600">
                  Action
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allFilteredRooms.map((room) => (
              <TableRow key={room.id} hover>
                <TableCell>
                  <Typography variant="body1" fontWeight="600">
                    {room.displayName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={room.status}
                    color={getStatusColor(room.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">{renderActionButton(room)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FrontDeskRooms;
